/**
 * セクション content の CTA リンクを以下の 2 つのルールで補正する:
 *
 * (1) 空 / "#" / null のリンク → 紐付けたフォームの公開 URL に自動置換
 * (2) 既に `/public/forms/<formId>` を指すリンク → `?campaignId=...` を自動付与
 *
 * これにより、エディタで CTA URL を入れ忘れていても、また Claude 生成時に
 * 仮の値が入っていても、フォーム送信が必ずキャンペーンに紐付く。
 */
import type { LandingAssignedCampaigns } from '@goenhub/shared';

/**
 * landing.assignedCampaigns から CTA フォールバック URL を取得。
 *
 * 1:N 対応: campaignId は焼き込まず、enrichContentWithFallbackCta の opts.campaignId で
 * 一元管理する（URL クエリの campaignId > sourceCampaignId の優先順位を一箇所で実装）。
 *
 * 注: campaignId / src=landing / cid / lid はすべて enrichContentWithFallbackCta 側で付与する。
 * 後方互換のため 2 番目の引数は受けるが、無視する（型シグネチャ維持）。
 */
export function getFallbackCtaHref(
  assigned: LandingAssignedCampaigns | undefined | null,
  _legacyCampaignId?: string | null,
): string | null {
  const formId = assigned?.formIds?.[0];
  if (!formId) return null;
  return `/public/forms/${formId}`;
}

/**
 * 既存リンクに campaignId / cid / lid / src=landing を注入。
 * - `/public/forms/...` を含むリンクのみ対象（外部 URL は変更なし）
 * - 既に付与済みのキーはスキップ（冪等）
 *
 * P1（campaign analytics）: LP 内のフォーム CTA に必ず ?src=landing を付け、
 * 受信側 submitForm が「LP 経由」と判定できるようにする。
 * cid/lid は LP に流入時に保持していれば、フォームでも個人特定の手がかりとして渡す。
 */
function injectFormParams(
  href: string,
  params: {
    campaignId?: string | null;
    cid?: string | null;
    lid?: string | null;
    src?: string | null;
    /** LP id。フォーム側で当該 LP の Google タグを解決するために伝搬する。 */
    lp?: string | null;
  },
): string {
  if (!href.includes('/public/forms/')) return href;
  const additions: Array<[string, string]> = [];
  if (params.campaignId && !/[?&]campaignId=/.test(href)) {
    additions.push(['campaignId', params.campaignId]);
  }
  if (params.cid && !/[?&]cid=/.test(href)) {
    additions.push(['cid', params.cid]);
  }
  if (params.lid && !/[?&]lid=/.test(href)) {
    additions.push(['lid', params.lid]);
  }
  if (params.src && !/[?&]src=/.test(href)) {
    additions.push(['src', params.src]);
  }
  if (params.lp && !/[?&]lp=/.test(href)) {
    additions.push(['lp', params.lp]);
  }
  if (additions.length === 0) return href;
  const [pathPart, hashPart] = href.split('#');
  const sep = pathPart.includes('?') ? '&' : '?';
  const query = additions
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
    .join('&');
  const newPath = `${pathPart}${sep}${query}`;
  return hashPart ? `${newPath}#${hashPart}` : newPath;
}

export interface CtaEnrichOptions {
  campaignId?: string | null;
  /** P1: LP 流入時の cid（ある場合）を form CTA に伝搬 */
  cid?: string | null;
  /** P1: LP 流入時の lid（ある場合）を form CTA に伝搬 */
  lid?: string | null;
  /** LP id。form CTA に ?lp= として伝搬し、フォーム側の Google タグ解決に使う。 */
  landingId?: string | null;
}

/**
 * セクション content 内の `*CtaHref` / `href` フィールドを再帰的に走査し:
 *  - 空なら fallbackHref に置換（P1: src=landing 付き）
 *  - 既存リンクで /public/forms/ を含むなら campaignId / cid / lid / src=landing を自動付与
 */
export function enrichContentWithFallbackCta(
  content: Record<string, unknown>,
  fallbackHref: string | null,
  options: CtaEnrichOptions | string | null = {},
): Record<string, unknown> {
  // 旧シグネチャ互換（campaignId を string で渡す呼び出しを許容）
  const opts: CtaEnrichOptions =
    typeof options === 'string' || options === null
      ? { campaignId: options }
      : options;
  // フォールバックリンクにも src=landing を付ける
  const enrichedFallback = fallbackHref
    ? injectFormParams(fallbackHref, { ...opts, src: 'landing', lp: opts.landingId })
    : null;
  return walkObject(value => value, enrichedFallback, opts, content) as Record<string, unknown>;
}

function walkObject(
  _identity: (v: unknown) => unknown,
  fallback: string | null,
  opts: CtaEnrichOptions,
  value: unknown,
): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => walkObject(_identity, fallback, opts, item));
  }
  if (value && typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    const next: Record<string, unknown> = {};
    for (const [key, v] of Object.entries(obj)) {
      // CTA リンクっぽいキー名のみを処理対象に
      const isCtaHrefKey = /CtaHref$/.test(key) || key === 'href' || key === 'ctaHref';
      if (isCtaHrefKey) {
        if (v === null || v === undefined || v === '' || v === '#') {
          // 空 → フォールバック（src=landing 付与済み）
          next[key] = fallback ?? v;
        } else if (typeof v === 'string') {
          // 既存リンクに campaignId / cid / lid / lp / src=landing を注入（条件マッチ時）
          next[key] = injectFormParams(v, { ...opts, src: 'landing', lp: opts.landingId });
        } else {
          next[key] = v;
        }
      } else {
        next[key] = walkObject(_identity, fallback, opts, v);
      }
    }
    return next;
  }
  return value;
}
